/* DIY Bakery — animations ported 1:1 from bernicebakery.com
   Stack: GSAP + ScrollTrigger + Lenis (same as the original).
   Values (durations, easings, offsets) match the original site:
   - words/lines: from y 101% / rotate 12deg, expo.out, 1.5s (words) / 1.2s (lines), delay 0.3
   - fades: from opacity 0 / y 4rem, expo.out, 1s, delay 0.5 (Grow adds scale 0.6)
   - window images: limit 20%/0 -> 100%/100% power2.out 0.75s, img scale 1.3 -> 1 power1.out 0.8s, stagger 0.15
   - hero scrub: back y 70%, img scale 1.4, wrapper radius 0 0 5rem 5rem + minHeight 90vh, content y -75%
   - parallax fronts: y -50% scrubbed over section transit; newsletter floats 50% / -100%
   - magnetic buttons: scale 1.2, expo.out 1.5s, follows cursor */

(function () {
  "use strict";

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || !window.gsap || !window.ScrollTrigger) return; // page stays static & fully visible

  gsap.registerPlugin(ScrollTrigger);

  const ENTER = "top bottom-=100px";
  /* frueher Ausloeser: feuert schon, bevor das Element im Bild ist — fuer
     Abschnitte, die beim schnellen Scrollen sonst uebersprungen werden */
  const ENTER_EARLY = "top bottom+=320px";

  /* elements already in the first viewport animate on load, without waiting for a scroll */
  const aboveFold = (el) => el.getBoundingClientRect().top < window.innerHeight - 100;

  /* attach a ScrollTrigger only when the element still has to be scrolled to */
  const withTrigger = (el, vars, start) => {
    if (!aboveFold(el)) vars.scrollTrigger = { trigger: el, start: start || ENTER };
    return vars;
  };

  /* ---------- Lenis smooth scroll ---------- */

  let lenis = null;
  if (window.Lenis) {
    document.documentElement.style.scrollBehavior = "auto";
    lenis = new Lenis();
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);

    document.querySelectorAll('a[href^="#"]').forEach((a) => {
      a.addEventListener("click", (e) => {
        const target = document.querySelector(a.getAttribute("href"));
        if (!target) return;
        e.preventDefault();
        lenis.scrollTo(target, { offset: -70 });
      });
    });
  }

  /* ---------- text splitting (Words / Lines) ---------- */

  function splitWords(el) {
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(" "));
              return;
            }
            const w = document.createElement("span");
            w.className = "Word";
            const inner = document.createElement("span");
            inner.className = "Word_Inner";
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && !child.classList.contains("Word")) {
          walk(child);
        }
      });
    };
    walk(el);
    return [...el.querySelectorAll(".Word_Inner")];
  }

  /* group split words into visual lines via offsetTop */
  function groupLines(words) {
    const lines = [];
    let curTop = null;
    let cur = [];
    words.forEach((w) => {
      const top = w.parentElement.offsetTop;
      if (curTop === null || Math.abs(top - curTop) < 6) {
        cur.push(w);
        if (curTop === null) curTop = top;
      } else {
        lines.push(cur);
        cur = [w];
        curTop = top;
      }
    });
    if (cur.length) lines.push(cur);
    return lines;
  }

  function initText() {
    document.querySelectorAll("[data-anim='words']").forEach((el) => {
      const words = splitWords(el);
      gsap.from(words, withTrigger(el, {
        y: "101%",
        rotation: 12,
        duration: 1.5,
        ease: "expo.out",
        delay: 0.3,
        stagger: 0.08,
      }));
    });

    document.querySelectorAll("[data-anim='lines']").forEach((el) => {
      const words = splitWords(el);
      groupLines(words).forEach((lineWords, i) => {
        gsap.from(lineWords, withTrigger(el, {
          y: "101%",
          rotation: 12,
          duration: 1.2,
          ease: "expo.out",
          delay: 0.3 + i * 0.1,
        }));
      });
    });
  }

  /* ---------- fades / grows ---------- */

  function initFades() {
    document.querySelectorAll(".anim-fade").forEach((el) => {
      /* data-anim-early: ohne Verzoegerung und frueher ausgeloest */
      const early = el.hasAttribute("data-anim-early");
      gsap.from(el, withTrigger(el, {
        opacity: 0,
        y: early ? "2rem" : "4rem",
        duration: early ? 0.6 : 1,
        delay: early ? 0 : 0.5,
        ease: "expo.out",
      }, early ? ENTER_EARLY : ENTER));
    });

    document.querySelectorAll(".anim-grow").forEach((el) => {
      gsap.from(el, withTrigger(el, {
        opacity: 0,
        y: "4rem",
        scale: 0.6,
        duration: 1,
        delay: 0.5,
        ease: "expo.out",
      }));
    });
  }

  /* ---------- window image reveals (arch masks) ---------- */

  function initWindows() {
    const groups = new Map();
    document.querySelectorAll(".win").forEach((win) => {
      if (win.classList.contains("dynamic")) return; // scrub-driven, handled separately
      const parent = win.closest(".product-grid") || win.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(win);
    });

    groups.forEach((wins) => {
      wins.forEach((win, i) => {
        const limit = win.querySelector(".win-limit");
        if (!limit) return;
        const imgs = limit.querySelectorAll("img");
        const delay = i * 0.15;
        const st = aboveFold(win) ? undefined : { trigger: win, start: ENTER };

        gsap.set(limit, { width: "20%", height: "0%" });
        gsap.set(imgs, { scale: 1.3 });

        gsap.to(limit, {
          width: "100%",
          height: "100%",
          duration: 0.75,
          delay,
          ease: "power2.out",
          scrollTrigger: st,
        });
        gsap.to(imgs, {
          scale: 1,
          duration: 0.8,
          delay,
          ease: "power1.out",
          scrollTrigger: st,
        });
      });
    });
  }

  /* ---------- hero scrub parallax ---------- */

  function initHero() {
    const hero = document.querySelector(".hero");
    if (!hero) return;
    const st = { trigger: hero, start: "top top", end: "bottom top", scrub: true };

    gsap.to(".hero-back", { y: "70%", ease: "none", scrollTrigger: st });
    gsap.to(".hero-back img", { scale: 1.4, ease: "none", scrollTrigger: st });
    gsap.to(hero, { borderRadius: "0 0 5rem 5rem", minHeight: "90vh", ease: "none", scrollTrigger: st });
    gsap.to(".hero-copy", { y: "-75%", ease: "none", scrollTrigger: st });
  }

  /* ---------- parallax fronts + newsletter floats ---------- */

  function initParallax() {
    document.querySelectorAll(".parallax-front").forEach((el) => {
      gsap.to(el, {
        y: "-50%",
        ease: "none",
        scrollTrigger: {
          trigger: el.closest("section") || el,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    const floatST = (el) => ({
      trigger: el.closest("section"),
      start: "top bottom",
      end: "bottom top",
      scrub: true,
    });
    document.querySelectorAll(".nl-float.left").forEach((el) => {
      gsap.to(el, { y: "50%", ease: "none", scrollTrigger: floatST(el) });
    });
    document.querySelectorAll(".nl-float.right").forEach((el) => {
      gsap.to(el, { y: "-100%", ease: "none", scrollTrigger: floatST(el) });
    });
  }

  /* .win.dynamic (celebrate arch) gets no size animation at all — the image
     stays at full size; only the text-on-path around it moves with the scroll. */

  /* ---------- arch text-on-path (scrubbed startOffset, like the original) ---------- */

  function initArchText() {
    document.querySelectorAll(".celebrate-path").forEach((svg) => {
      /* getElementsByTagName: querySelector can't match SVG's case-sensitive <textPath> */
      const tp = svg.getElementsByTagName("textPath")[0];
      if (!tp) return;
      gsap.to(tp, {
        attr: { startOffset: 20 },
        ease: "none",
        scrollTrigger: {
          trigger: svg,
          start: "top bottom+=40",
          end: "bottom top",
          scrub: true,
        },
      });
    });
  }

  /* ---------- magnetic buttons ---------- */

  function initMagnetic() {
    /* Frueher wanderten die Buttons dem Zeiger hinterher und wichen beim
       Anvisieren aus. Jetzt nur noch ein leichtes Anwachsen als Rueckmeldung,
       die Schaltflaeche bleibt, wo sie ist. */
    document.querySelectorAll(".btn-magnetic").forEach((btn) => {
      btn.addEventListener("mouseenter", () => {
        gsap.to(btn, { scale: 1.06, duration: 0.45, ease: "expo.out" });
      });
      btn.addEventListener("mouseleave", () => {
        gsap.to(btn, { scale: 1, duration: 0.45, ease: "expo.out" });
      });
    });
  }

  /* ---------- boot ---------- */

  function init() {
    initText();
    initFades();
    initWindows();
    initHero();
    initParallax();
    initArchText();
    initMagnetic();
    ScrollTrigger.refresh();
  }

  /* run right away so above-the-fold animations start on load, not on first scroll.
     Guard: only boot once the viewport reports a real height — with 0 (hidden tab,
     prerender) every above-fold check would fail and everything would wait on scroll. */
  let booted = false;
  function tryBoot() {
    if (booted || window.innerHeight === 0) return;
    booted = true;
    init();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
  }

  tryBoot();
  if (!booted) {
    const poll = setInterval(() => {
      tryBoot();
      if (booted) clearInterval(poll);
    }, 100);
    window.addEventListener("resize", tryBoot);
    document.addEventListener("visibilitychange", tryBoot);
  }

  window.addEventListener("load", () => ScrollTrigger.refresh());
})();
